import type { ConnectionPropertiesSchema } from "../../data-sources/schemas";

export type AutoInitiateAction = { type: "redirect"; path: string } | { type: "expand-form"; connectionId: number };

/**
 * What to do automatically when the panel opens, based on how many connections have an actionable
 * reconnect:
 * - none, or more than one: nothing — the list is shown so the user picks.
 * - exactly one: expand its form when the connector reconnects by form (`supportsFormReconnect`
 *   and a `reconnectPostPath`), otherwise redirect via its `reconnectPath`.
 *
 * The form is preferred over the redirect when both are available: connectors that provide a
 * reconnect form (Magento, BigCommerce) reconnected via POST on the old page, and their redirect
 * auth flows are not the production connect method.
 *
 * `hasOauthError` must be true when the panel has just reopened after a failed redirect attempt
 * (an `oauth-error` on the URL) — otherwise a persistently-broken connection would auto-redirect,
 * fail, reopen, and auto-redirect again with no user action in between.
 */
export const getAutoInitiateAction = (
  connections: ConnectionPropertiesSchema[],
  hasOauthError: boolean,
  supportsFormReconnect: boolean
): AutoInitiateAction | undefined => {
  if (hasOauthError) {
    return undefined;
  }
  const actionable = connections.filter(
    (connection) =>
      !connection.isConnected && (connection.reconnectPath || (supportsFormReconnect && connection.reconnectPostPath))
  );
  if (actionable.length !== 1) {
    return undefined;
  }
  const [only] = actionable;
  if (supportsFormReconnect && only.reconnectPostPath) {
    return { type: "expand-form", connectionId: only.id };
  }
  return { type: "redirect", path: only.reconnectPath as string };
};
